import * as z from 'zod';

export const CreateAccountRequest = z.object({
  contactId: z
    .string()
    .describe(
      "Contact ID for a site's contact. See the Contacts API for more information."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const CreateAccountResponse = z.object({
  account: z
    .object({
      _id: z
        .string()
        .describe('Account ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      contactId: z
        .string()
        .describe(
          "Account owner's contact ID. See the Contacts API to learn more about a site's contacts."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      memberId: z
        .string()
        .describe(
          "Member ID of the account owner. See the Members API to learn more about a site's members."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      points: z
        .object({
          balance: z
            .number()
            .int()
            .describe(
              'Current points balance.\n\nEqual to the sum of `earned` and `adjusted` points, minus `redeemed` points.'
            )
            .min(0)
            .optional(),
          earned: z
            .number()
            .int()
            .describe('Total earned points.')
            .min(0)
            .optional(),
          adjusted: z
            .number()
            .int()
            .describe('Total adjusted points.')
            .min(0)
            .optional(),
          redeemed: z
            .number()
            .int()
            .describe('Total redeemed points.')
            .min(0)
            .optional(),
          expired: z
            .number()
            .int()
            .describe('Total expired points.')
            .min(0)
            .optional(),
        })
        .describe("Details of the account's point totals.")
        .optional(),
      rewardAvailable: z
        .boolean()
        .describe(
          'Whether the account has enough points in `points.balance` to redeem for a reward.'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the account was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the account was last updated.')
        .optional()
        .nullable(),
      lastActivityDate: z
        .date()
        .describe("Date and time of the account's last activity.")
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          'Revision number, which increments by 1 each time the loyalty account is updated.\n\nTo prevent conflicting changes, the current `revision` must be passed when updating the loyalty account.\n\nIgnored when creating an account.'
        )
        .optional(),
      tier: z
        .object({
          _id: z
            .string()
            .describe(
              'Tier ID.\n\nThis field will not be returned if the account belongs to the base tier.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe(
              'Date and time the tier information was last recalculated.'
            )
            .optional()
            .nullable(),
          points: z
            .number()
            .int()
            .describe(
              "Points earned and adjusted over the tier's current rolling window."
            )
            .min(0)
            .optional(),
        })
        .describe(
          'Tier information.\n\nSee the Tiers API for more information.'
        )
        .optional(),
      contact: z
        .object({
          _id: z
            .string()
            .describe('Contact ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          name: z
            .string()
            .describe("Contact's first and last name.")
            .optional()
            .nullable(),
          picture: z
            .object({
              _id: z.string().describe('WixMedia image ID.').optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
            })
            .describe("Contact's profile picture.")
            .optional(),
          email: z
            .string()
            .describe("Contact's email address.")
            .email()
            .optional()
            .nullable(),
          displayName: z
            .string()
            .describe(
              "Contact's first and last name. If no name is provided, uses contact's email."
            )
            .optional()
            .nullable(),
        })
        .describe('Contact information.')
        .optional(),
      pointsExpiration: z
        .object({
          expirationDate: z
            .date()
            .describe('Date and time at which points are expiring.')
            .optional()
            .nullable(),
          expiringPointsAmount: z
            .number()
            .int()
            .describe('Amount of points that are going to expire at that date.')
            .optional(),
        })
        .describe('Points expiration information.')
        .optional(),
    })
    .describe('Created loyalty account.')
    .optional(),
});
export const EarnPointsRequest = z.object({
  accountId: z
    .string()
    .describe('Loyalty account ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .intersection(
      z.object({
        amount: z
          .number()
          .int()
          .describe(
            'Amount of points to earn. Must be a positive, whole number.\n\nMin: `1`\n\nMax: `9999999`'
          )
          .min(1)
          .max(9999999)
          .optional(),
        description: z
          .string()
          .describe(
            'Description of how the points were earned.\n\nMax: 100 characters'
          )
          .min(1)
          .max(100)
          .optional(),
        appId: z
          .string()
          .describe(
            "ID of the app that initiated the transaction.\n\nIf points were earned manually, then the `appId` is the Loyalty app's\n`wixAppId` of `553c79f3-5625-4f38-b14b-ef7c0d1e87df`. If points were earned in an automatic event,\nthen the `appId` is from that automation's `sourceAppId`."
          )
          .min(1)
          .max(100),
        idempotencyKey: z
          .string()
          .describe(
            'Unique string identifier generated by the app. Wix uses this identifier to recognize subsequent retries of the same request.\n\nPlease use `GUID` format.'
          )
          .min(1)
          .max(256),
        activityType: z
          .string()
          .describe(
            'Activity type.\n\nIf points were earned through automation it should be set to the trigger key.'
          )
          .max(100)
          .optional()
          .nullable(),
        orderId: z
          .string()
          .describe('Order ID which was source of this transaction.')
          .max(512)
          .optional()
          .nullable(),
        limitApplied: z
          .boolean()
          .describe(
            'Whether the points amount was adjusted by an earn points limit provider.'
          )
          .optional(),
      }),
      z.xor([
        z.object({ followedSocialMedia: z.never().optional() }),
        z.object({
          followedSocialMedia: z
            .object({
              channel: z
                .string()
                .describe(
                  'Social media channel.\nExample: `FACEBOOK`, `INSTAGRAM`.'
                )
                .max(32)
                .optional(),
            })
            .describe('Followed social media details.'),
        }),
      ])
    )
    .describe('Earn points info.'),
});
export const EarnPointsResponse = z.object({
  account: z
    .object({
      _id: z
        .string()
        .describe('Account ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      contactId: z
        .string()
        .describe(
          "Account owner's contact ID. See the Contacts API to learn more about a site's contacts."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      memberId: z
        .string()
        .describe(
          "Member ID of the account owner. See the Members API to learn more about a site's members."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      points: z
        .object({
          balance: z
            .number()
            .int()
            .describe(
              'Current points balance.\n\nEqual to the sum of `earned` and `adjusted` points, minus `redeemed` points.'
            )
            .min(0)
            .optional(),
          earned: z
            .number()
            .int()
            .describe('Total earned points.')
            .min(0)
            .optional(),
          adjusted: z
            .number()
            .int()
            .describe('Total adjusted points.')
            .min(0)
            .optional(),
          redeemed: z
            .number()
            .int()
            .describe('Total redeemed points.')
            .min(0)
            .optional(),
          expired: z
            .number()
            .int()
            .describe('Total expired points.')
            .min(0)
            .optional(),
        })
        .describe("Details of the account's point totals.")
        .optional(),
      rewardAvailable: z
        .boolean()
        .describe(
          'Whether the account has enough points in `points.balance` to redeem for a reward.'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the account was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the account was last updated.')
        .optional()
        .nullable(),
      lastActivityDate: z
        .date()
        .describe("Date and time of the account's last activity.")
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          'Revision number, which increments by 1 each time the loyalty account is updated.\n\nTo prevent conflicting changes, the current `revision` must be passed when updating the loyalty account.\n\nIgnored when creating an account.'
        )
        .optional(),
      tier: z
        .object({
          _id: z
            .string()
            .describe(
              'Tier ID.\n\nThis field will not be returned if the account belongs to the base tier.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe(
              'Date and time the tier information was last recalculated.'
            )
            .optional()
            .nullable(),
          points: z
            .number()
            .int()
            .describe(
              "Points earned and adjusted over the tier's current rolling window."
            )
            .min(0)
            .optional(),
        })
        .describe(
          'Tier information.\n\nSee the Tiers API for more information.'
        )
        .optional(),
      contact: z
        .object({
          _id: z
            .string()
            .describe('Contact ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          name: z
            .string()
            .describe("Contact's first and last name.")
            .optional()
            .nullable(),
          picture: z
            .object({
              _id: z.string().describe('WixMedia image ID.').optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
            })
            .describe("Contact's profile picture.")
            .optional(),
          email: z
            .string()
            .describe("Contact's email address.")
            .email()
            .optional()
            .nullable(),
          displayName: z
            .string()
            .describe(
              "Contact's first and last name. If no name is provided, uses contact's email."
            )
            .optional()
            .nullable(),
        })
        .describe('Contact information.')
        .optional(),
      pointsExpiration: z
        .object({
          expirationDate: z
            .date()
            .describe('Date and time at which points are expiring.')
            .optional()
            .nullable(),
          expiringPointsAmount: z
            .number()
            .int()
            .describe('Amount of points that are going to expire at that date.')
            .optional(),
        })
        .describe('Points expiration information.')
        .optional(),
    })
    .describe('Updated loyalty account.')
    .optional(),
  transactionId: z
    .string()
    .describe('Transaction ID associated with the points earned.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const AdjustPointsRequest = z.object({
  accountId: z
    .string()
    .describe('Loyalty account ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .intersection(
      z.object({
        description: z
          .string()
          .describe(
            'Description to explain the reason for the points adjustment.'
          )
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^\d+$/, 'Must be a valid UInt64 string')
          .describe(
            "Each time the loyalty account is updated, `revision` increments by 1.\n\nThe current `revision` must be passed when adjusting points in the loyalty account. This\nensures you're working with the latest version of the loyalty account and prevents unintended overwrites."
          )
          .optional(),
      }),
      z.xor([
        z.object({
          balance: z.never().optional(),
          amount: z.never().optional(),
        }),
        z.object({
          amount: z.never().optional(),
          balance: z
            .number()
            .int()
            .describe(
              "Sets the account's point balance to this amount. Must be a positive, whole number or zero.\n\nThe net difference between this new balance and the previous balance will be reflected in the `adjusted` field of the customer's account.\n\nMin: `0`\n\nMax: `999999999`"
            )
            .min(0)
            .max(999999999),
        }),
        z.object({
          balance: z.never().optional(),
          amount: z
            .number()
            .int()
            .describe(
              "Adjusts the account's point balance by this amount. Must be a whole number with a maximum of 7 digits.\nThe amount can be negative, but cannot be `0`.\n\nMin: `-9999999`\n\nMax: `9999999`"
            )
            .min(-9999999)
            .max(9999999),
        }),
      ])
    )
    .describe('Options to use when adjusting points.')
    .optional(),
});
export const AdjustPointsResponse = z.object({
  account: z
    .object({
      _id: z
        .string()
        .describe('Account ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      contactId: z
        .string()
        .describe(
          "Account owner's contact ID. See the Contacts API to learn more about a site's contacts."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      memberId: z
        .string()
        .describe(
          "Member ID of the account owner. See the Members API to learn more about a site's members."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      points: z
        .object({
          balance: z
            .number()
            .int()
            .describe(
              'Current points balance.\n\nEqual to the sum of `earned` and `adjusted` points, minus `redeemed` points.'
            )
            .min(0)
            .optional(),
          earned: z
            .number()
            .int()
            .describe('Total earned points.')
            .min(0)
            .optional(),
          adjusted: z
            .number()
            .int()
            .describe('Total adjusted points.')
            .min(0)
            .optional(),
          redeemed: z
            .number()
            .int()
            .describe('Total redeemed points.')
            .min(0)
            .optional(),
          expired: z
            .number()
            .int()
            .describe('Total expired points.')
            .min(0)
            .optional(),
        })
        .describe("Details of the account's point totals.")
        .optional(),
      rewardAvailable: z
        .boolean()
        .describe(
          'Whether the account has enough points in `points.balance` to redeem for a reward.'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the account was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the account was last updated.')
        .optional()
        .nullable(),
      lastActivityDate: z
        .date()
        .describe("Date and time of the account's last activity.")
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          'Revision number, which increments by 1 each time the loyalty account is updated.\n\nTo prevent conflicting changes, the current `revision` must be passed when updating the loyalty account.\n\nIgnored when creating an account.'
        )
        .optional(),
      tier: z
        .object({
          _id: z
            .string()
            .describe(
              'Tier ID.\n\nThis field will not be returned if the account belongs to the base tier.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe(
              'Date and time the tier information was last recalculated.'
            )
            .optional()
            .nullable(),
          points: z
            .number()
            .int()
            .describe(
              "Points earned and adjusted over the tier's current rolling window."
            )
            .min(0)
            .optional(),
        })
        .describe(
          'Tier information.\n\nSee the Tiers API for more information.'
        )
        .optional(),
      contact: z
        .object({
          _id: z
            .string()
            .describe('Contact ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          name: z
            .string()
            .describe("Contact's first and last name.")
            .optional()
            .nullable(),
          picture: z
            .object({
              _id: z.string().describe('WixMedia image ID.').optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
            })
            .describe("Contact's profile picture.")
            .optional(),
          email: z
            .string()
            .describe("Contact's email address.")
            .email()
            .optional()
            .nullable(),
          displayName: z
            .string()
            .describe(
              "Contact's first and last name. If no name is provided, uses contact's email."
            )
            .optional()
            .nullable(),
        })
        .describe('Contact information.')
        .optional(),
      pointsExpiration: z
        .object({
          expirationDate: z
            .date()
            .describe('Date and time at which points are expiring.')
            .optional()
            .nullable(),
          expiringPointsAmount: z
            .number()
            .int()
            .describe('Amount of points that are going to expire at that date.')
            .optional(),
        })
        .describe('Points expiration information.')
        .optional(),
    })
    .describe('Loyalty account.')
    .optional(),
  transactionId: z
    .string()
    .describe('Transaction ID associated with the points adjustment.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const GetAccountRequest = z.object({
  _id: z
    .string()
    .describe('ID of the account to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetAccountResponse = z.object({
  _id: z
    .string()
    .describe('Account ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  contactId: z
    .string()
    .describe(
      "Account owner's contact ID. See the Contacts API to learn more about a site's contacts."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  memberId: z
    .string()
    .describe(
      "Member ID of the account owner. See the Members API to learn more about a site's members."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  points: z
    .object({
      balance: z
        .number()
        .int()
        .describe(
          'Current points balance.\n\nEqual to the sum of `earned` and `adjusted` points, minus `redeemed` points.'
        )
        .min(0)
        .optional(),
      earned: z
        .number()
        .int()
        .describe('Total earned points.')
        .min(0)
        .optional(),
      adjusted: z
        .number()
        .int()
        .describe('Total adjusted points.')
        .min(0)
        .optional(),
      redeemed: z
        .number()
        .int()
        .describe('Total redeemed points.')
        .min(0)
        .optional(),
      expired: z
        .number()
        .int()
        .describe('Total expired points.')
        .min(0)
        .optional(),
    })
    .describe("Details of the account's point totals.")
    .optional(),
  rewardAvailable: z
    .boolean()
    .describe(
      'Whether the account has enough points in `points.balance` to redeem for a reward.'
    )
    .optional(),
  _createdDate: z
    .date()
    .describe('Date and time the account was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the account was last updated.')
    .optional()
    .nullable(),
  lastActivityDate: z
    .date()
    .describe("Date and time of the account's last activity.")
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^\d+$/, 'Must be a valid UInt64 string')
    .describe(
      'Revision number, which increments by 1 each time the loyalty account is updated.\n\nTo prevent conflicting changes, the current `revision` must be passed when updating the loyalty account.\n\nIgnored when creating an account.'
    )
    .optional(),
  tier: z
    .object({
      _id: z
        .string()
        .describe(
          'Tier ID.\n\nThis field will not be returned if the account belongs to the base tier.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the tier information was last recalculated.')
        .optional()
        .nullable(),
      points: z
        .number()
        .int()
        .describe(
          "Points earned and adjusted over the tier's current rolling window."
        )
        .min(0)
        .optional(),
    })
    .describe('Tier information.\n\nSee the Tiers API for more information.')
    .optional(),
  contact: z
    .object({
      _id: z
        .string()
        .describe('Contact ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe("Contact's first and last name.")
        .optional()
        .nullable(),
      picture: z
        .object({
          _id: z.string().describe('WixMedia image ID.').optional(),
          url: z.string().describe('Image URL.').optional(),
          height: z
            .number()
            .int()
            .describe('Original image height.')
            .optional(),
          width: z.number().int().describe('Original image width.').optional(),
        })
        .describe("Contact's profile picture.")
        .optional(),
      email: z
        .string()
        .describe("Contact's email address.")
        .email()
        .optional()
        .nullable(),
      displayName: z
        .string()
        .describe(
          "Contact's first and last name. If no name is provided, uses contact's email."
        )
        .optional()
        .nullable(),
    })
    .describe('Contact information.')
    .optional(),
  pointsExpiration: z
    .object({
      expirationDate: z
        .date()
        .describe('Date and time at which points are expiring.')
        .optional()
        .nullable(),
      expiringPointsAmount: z
        .number()
        .int()
        .describe('Amount of points that are going to expire at that date.')
        .optional(),
    })
    .describe('Points expiration information.')
    .optional(),
});
export const QueryLoyaltyAccountsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          contactId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          memberId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'points.balance': z
            .object({
              $eq: z.number(),
              $exists: z.boolean(),
              $gt: z.number(),
              $gte: z.number(),
              $hasAll: z.array(z.number()),
              $hasSome: z.array(z.number()),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
              $nin: z.array(z.number()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'points.earned': z
            .object({
              $eq: z.number(),
              $exists: z.boolean(),
              $gt: z.number(),
              $gte: z.number(),
              $hasAll: z.array(z.number()),
              $hasSome: z.array(z.number()),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
              $nin: z.array(z.number()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'points.adjusted': z
            .object({
              $eq: z.number(),
              $exists: z.boolean(),
              $gt: z.number(),
              $gte: z.number(),
              $hasAll: z.array(z.number()),
              $hasSome: z.array(z.number()),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
              $nin: z.array(z.number()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'points.redeemed': z
            .object({
              $eq: z.number(),
              $exists: z.boolean(),
              $gt: z.number(),
              $gte: z.number(),
              $hasAll: z.array(z.number()),
              $hasSome: z.array(z.number()),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
              $nin: z.array(z.number()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'points.expired': z
            .object({
              $eq: z.number(),
              $exists: z.boolean(),
              $gt: z.number(),
              $gte: z.number(),
              $hasAll: z.array(z.number()),
              $hasSome: z.array(z.number()),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
              $nin: z.array(z.number()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          lastActivityDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'tier._id': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'pointsExpiration.expirationDate': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'pointsExpiration.expiringPointsAmount': z
            .object({
              $eq: z.number(),
              $exists: z.boolean(),
              $gt: z.number(),
              $gte: z.number(),
              $hasAll: z.array(z.number()),
              $hasSome: z.array(z.number()),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
              $nin: z.array(z.number()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                'contactId',
                'memberId',
                'points.balance',
                'points.earned',
                'points.adjusted',
                'points.redeemed',
                'points.expired',
                '_createdDate',
                'lastActivityDate',
                'tier._id',
                'pointsExpiration.expirationDate',
                'pointsExpiration.expiringPointsAmount',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe(
      'Filter object.\nSee [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more information.'
    ),
});
export const QueryLoyaltyAccountsResponse = z.object({
  loyaltyAccounts: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Account ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        contactId: z
          .string()
          .describe(
            "Account owner's contact ID. See the Contacts API to learn more about a site's contacts."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        memberId: z
          .string()
          .describe(
            "Member ID of the account owner. See the Members API to learn more about a site's members."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        points: z
          .object({
            balance: z
              .number()
              .int()
              .describe(
                'Current points balance.\n\nEqual to the sum of `earned` and `adjusted` points, minus `redeemed` points.'
              )
              .min(0)
              .optional(),
            earned: z
              .number()
              .int()
              .describe('Total earned points.')
              .min(0)
              .optional(),
            adjusted: z
              .number()
              .int()
              .describe('Total adjusted points.')
              .min(0)
              .optional(),
            redeemed: z
              .number()
              .int()
              .describe('Total redeemed points.')
              .min(0)
              .optional(),
            expired: z
              .number()
              .int()
              .describe('Total expired points.')
              .min(0)
              .optional(),
          })
          .describe("Details of the account's point totals.")
          .optional(),
        rewardAvailable: z
          .boolean()
          .describe(
            'Whether the account has enough points in `points.balance` to redeem for a reward.'
          )
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time the account was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the account was last updated.')
          .optional()
          .nullable(),
        lastActivityDate: z
          .date()
          .describe("Date and time of the account's last activity.")
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^\d+$/, 'Must be a valid UInt64 string')
          .describe(
            'Revision number, which increments by 1 each time the loyalty account is updated.\n\nTo prevent conflicting changes, the current `revision` must be passed when updating the loyalty account.\n\nIgnored when creating an account.'
          )
          .optional(),
        tier: z
          .object({
            _id: z
              .string()
              .describe(
                'Tier ID.\n\nThis field will not be returned if the account belongs to the base tier.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date and time the tier information was last recalculated.'
              )
              .optional()
              .nullable(),
            points: z
              .number()
              .int()
              .describe(
                "Points earned and adjusted over the tier's current rolling window."
              )
              .min(0)
              .optional(),
          })
          .describe(
            'Tier information.\n\nSee the Tiers API for more information.'
          )
          .optional(),
        contact: z
          .object({
            _id: z
              .string()
              .describe('Contact ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe("Contact's first and last name.")
              .optional()
              .nullable(),
            picture: z
              .object({
                _id: z.string().describe('WixMedia image ID.').optional(),
                url: z.string().describe('Image URL.').optional(),
                height: z
                  .number()
                  .int()
                  .describe('Original image height.')
                  .optional(),
                width: z
                  .number()
                  .int()
                  .describe('Original image width.')
                  .optional(),
              })
              .describe("Contact's profile picture.")
              .optional(),
            email: z
              .string()
              .describe("Contact's email address.")
              .email()
              .optional()
              .nullable(),
            displayName: z
              .string()
              .describe(
                "Contact's first and last name. If no name is provided, uses contact's email."
              )
              .optional()
              .nullable(),
          })
          .describe('Contact information.')
          .optional(),
        pointsExpiration: z
          .object({
            expirationDate: z
              .date()
              .describe('Date and time at which points are expiring.')
              .optional()
              .nullable(),
            expiringPointsAmount: z
              .number()
              .int()
              .describe(
                'Amount of points that are going to expire at that date.'
              )
              .optional(),
          })
          .describe('Points expiration information.')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata')
    .optional(),
});
export const GetProgramTotalsRequest = z.object({});
export const GetProgramTotalsResponse = z.object({
  points: z
    .object({
      balance: z
        .number()
        .int()
        .describe(
          'Current points balance.\n\nEqual to the sum of `earned` and `adjusted` points, minus `redeemed` points.'
        )
        .min(0)
        .optional(),
      earned: z
        .number()
        .int()
        .describe('Total earned points.')
        .min(0)
        .optional(),
      adjusted: z
        .number()
        .int()
        .describe('Total adjusted points.')
        .min(0)
        .optional(),
      redeemed: z
        .number()
        .int()
        .describe('Total redeemed points.')
        .min(0)
        .optional(),
      expired: z
        .number()
        .int()
        .describe('Total expired points.')
        .min(0)
        .optional(),
    })
    .describe('Point totals for the entire program.')
    .optional(),
  tierTotals: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'Tier ID. If no Tier ID is provided, the ID is `null`.\nSee the base tier in the Tiers API for more information.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        numberOfAccounts: z
          .number()
          .int()
          .describe(
            'Total number of loyalty account that belong to a particular tier.'
          )
          .min(0)
          .optional(),
      })
    )
    .optional(),
});
export const GetCurrentMemberAccountRequest = z.object({});
export const GetCurrentMemberAccountResponse = z.object({
  account: z
    .object({
      _id: z
        .string()
        .describe('Account ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      contactId: z
        .string()
        .describe(
          "Account owner's contact ID. See the Contacts API to learn more about a site's contacts."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      memberId: z
        .string()
        .describe(
          "Member ID of the account owner. See the Members API to learn more about a site's members."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      points: z
        .object({
          balance: z
            .number()
            .int()
            .describe(
              'Current points balance.\n\nEqual to the sum of `earned` and `adjusted` points, minus `redeemed` points.'
            )
            .min(0)
            .optional(),
          earned: z
            .number()
            .int()
            .describe('Total earned points.')
            .min(0)
            .optional(),
          adjusted: z
            .number()
            .int()
            .describe('Total adjusted points.')
            .min(0)
            .optional(),
          redeemed: z
            .number()
            .int()
            .describe('Total redeemed points.')
            .min(0)
            .optional(),
          expired: z
            .number()
            .int()
            .describe('Total expired points.')
            .min(0)
            .optional(),
        })
        .describe("Details of the account's point totals.")
        .optional(),
      rewardAvailable: z
        .boolean()
        .describe(
          'Whether the account has enough points in `points.balance` to redeem for a reward.'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the account was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the account was last updated.')
        .optional()
        .nullable(),
      lastActivityDate: z
        .date()
        .describe("Date and time of the account's last activity.")
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          'Revision number, which increments by 1 each time the loyalty account is updated.\n\nTo prevent conflicting changes, the current `revision` must be passed when updating the loyalty account.\n\nIgnored when creating an account.'
        )
        .optional(),
      tier: z
        .object({
          _id: z
            .string()
            .describe(
              'Tier ID.\n\nThis field will not be returned if the account belongs to the base tier.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe(
              'Date and time the tier information was last recalculated.'
            )
            .optional()
            .nullable(),
          points: z
            .number()
            .int()
            .describe(
              "Points earned and adjusted over the tier's current rolling window."
            )
            .min(0)
            .optional(),
        })
        .describe(
          'Tier information.\n\nSee the Tiers API for more information.'
        )
        .optional(),
      contact: z
        .object({
          _id: z
            .string()
            .describe('Contact ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          name: z
            .string()
            .describe("Contact's first and last name.")
            .optional()
            .nullable(),
          picture: z
            .object({
              _id: z.string().describe('WixMedia image ID.').optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
            })
            .describe("Contact's profile picture.")
            .optional(),
          email: z
            .string()
            .describe("Contact's email address.")
            .email()
            .optional()
            .nullable(),
          displayName: z
            .string()
            .describe(
              "Contact's first and last name. If no name is provided, uses contact's email."
            )
            .optional()
            .nullable(),
        })
        .describe('Contact information.')
        .optional(),
      pointsExpiration: z
        .object({
          expirationDate: z
            .date()
            .describe('Date and time at which points are expiring.')
            .optional()
            .nullable(),
          expiringPointsAmount: z
            .number()
            .int()
            .describe('Amount of points that are going to expire at that date.')
            .optional(),
        })
        .describe('Points expiration information.')
        .optional(),
    })
    .describe('Loyalty account.')
    .optional(),
});
export const GetAccountBySecondaryIdRequest = z.object({
  options: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({
          contactId: z.never().optional(),
          memberId: z.never().optional(),
        }),
        z.object({
          memberId: z.never().optional(),
          contactId: z
            .string()
            .describe(
              "Account owner's contact ID. See the Contacts API for more information."
            )
            .max(50),
        }),
        z.object({
          contactId: z.never().optional(),
          memberId: z
            .string()
            .describe(
              "Account owner's member ID. See the Members API for more information."
            )
            .max(50),
        }),
      ])
    )
    .describe('ID of the customer to retrieve loyalty account for.'),
});
export const GetAccountBySecondaryIdResponse = z.object({
  account: z
    .object({
      _id: z
        .string()
        .describe('Account ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      contactId: z
        .string()
        .describe(
          "Account owner's contact ID. See the Contacts API to learn more about a site's contacts."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      memberId: z
        .string()
        .describe(
          "Member ID of the account owner. See the Members API to learn more about a site's members."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      points: z
        .object({
          balance: z
            .number()
            .int()
            .describe(
              'Current points balance.\n\nEqual to the sum of `earned` and `adjusted` points, minus `redeemed` points.'
            )
            .min(0)
            .optional(),
          earned: z
            .number()
            .int()
            .describe('Total earned points.')
            .min(0)
            .optional(),
          adjusted: z
            .number()
            .int()
            .describe('Total adjusted points.')
            .min(0)
            .optional(),
          redeemed: z
            .number()
            .int()
            .describe('Total redeemed points.')
            .min(0)
            .optional(),
          expired: z
            .number()
            .int()
            .describe('Total expired points.')
            .min(0)
            .optional(),
        })
        .describe("Details of the account's point totals.")
        .optional(),
      rewardAvailable: z
        .boolean()
        .describe(
          'Whether the account has enough points in `points.balance` to redeem for a reward.'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the account was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the account was last updated.')
        .optional()
        .nullable(),
      lastActivityDate: z
        .date()
        .describe("Date and time of the account's last activity.")
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          'Revision number, which increments by 1 each time the loyalty account is updated.\n\nTo prevent conflicting changes, the current `revision` must be passed when updating the loyalty account.\n\nIgnored when creating an account.'
        )
        .optional(),
      tier: z
        .object({
          _id: z
            .string()
            .describe(
              'Tier ID.\n\nThis field will not be returned if the account belongs to the base tier.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe(
              'Date and time the tier information was last recalculated.'
            )
            .optional()
            .nullable(),
          points: z
            .number()
            .int()
            .describe(
              "Points earned and adjusted over the tier's current rolling window."
            )
            .min(0)
            .optional(),
        })
        .describe(
          'Tier information.\n\nSee the Tiers API for more information.'
        )
        .optional(),
      contact: z
        .object({
          _id: z
            .string()
            .describe('Contact ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          name: z
            .string()
            .describe("Contact's first and last name.")
            .optional()
            .nullable(),
          picture: z
            .object({
              _id: z.string().describe('WixMedia image ID.').optional(),
              url: z.string().describe('Image URL.').optional(),
              height: z
                .number()
                .int()
                .describe('Original image height.')
                .optional(),
              width: z
                .number()
                .int()
                .describe('Original image width.')
                .optional(),
            })
            .describe("Contact's profile picture.")
            .optional(),
          email: z
            .string()
            .describe("Contact's email address.")
            .email()
            .optional()
            .nullable(),
          displayName: z
            .string()
            .describe(
              "Contact's first and last name. If no name is provided, uses contact's email."
            )
            .optional()
            .nullable(),
        })
        .describe('Contact information.')
        .optional(),
      pointsExpiration: z
        .object({
          expirationDate: z
            .date()
            .describe('Date and time at which points are expiring.')
            .optional()
            .nullable(),
          expiringPointsAmount: z
            .number()
            .int()
            .describe('Amount of points that are going to expire at that date.')
            .optional(),
        })
        .describe('Points expiration information.')
        .optional(),
    })
    .describe('Loyalty account.')
    .optional(),
});
export const ListAccountsRequest = z.object({
  options: z
    .object({
      contactIds: z.array(z.string()).max(50).optional(),
      cursorPaging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Maximum number of items to return in the results.')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Pagination options.')
        .optional(),
    })
    .describe('Options to use when retrieving a list of loyalty accounts.')
    .optional(),
});
export const ListAccountsResponse = z.object({
  accounts: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Account ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        contactId: z
          .string()
          .describe(
            "Account owner's contact ID. See the Contacts API to learn more about a site's contacts."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        memberId: z
          .string()
          .describe(
            "Member ID of the account owner. See the Members API to learn more about a site's members."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        points: z
          .object({
            balance: z
              .number()
              .int()
              .describe(
                'Current points balance.\n\nEqual to the sum of `earned` and `adjusted` points, minus `redeemed` points.'
              )
              .min(0)
              .optional(),
            earned: z
              .number()
              .int()
              .describe('Total earned points.')
              .min(0)
              .optional(),
            adjusted: z
              .number()
              .int()
              .describe('Total adjusted points.')
              .min(0)
              .optional(),
            redeemed: z
              .number()
              .int()
              .describe('Total redeemed points.')
              .min(0)
              .optional(),
            expired: z
              .number()
              .int()
              .describe('Total expired points.')
              .min(0)
              .optional(),
          })
          .describe("Details of the account's point totals.")
          .optional(),
        rewardAvailable: z
          .boolean()
          .describe(
            'Whether the account has enough points in `points.balance` to redeem for a reward.'
          )
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time the account was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the account was last updated.')
          .optional()
          .nullable(),
        lastActivityDate: z
          .date()
          .describe("Date and time of the account's last activity.")
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^\d+$/, 'Must be a valid UInt64 string')
          .describe(
            'Revision number, which increments by 1 each time the loyalty account is updated.\n\nTo prevent conflicting changes, the current `revision` must be passed when updating the loyalty account.\n\nIgnored when creating an account.'
          )
          .optional(),
        tier: z
          .object({
            _id: z
              .string()
              .describe(
                'Tier ID.\n\nThis field will not be returned if the account belongs to the base tier.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date and time the tier information was last recalculated.'
              )
              .optional()
              .nullable(),
            points: z
              .number()
              .int()
              .describe(
                "Points earned and adjusted over the tier's current rolling window."
              )
              .min(0)
              .optional(),
          })
          .describe(
            'Tier information.\n\nSee the Tiers API for more information.'
          )
          .optional(),
        contact: z
          .object({
            _id: z
              .string()
              .describe('Contact ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe("Contact's first and last name.")
              .optional()
              .nullable(),
            picture: z
              .object({
                _id: z.string().describe('WixMedia image ID.').optional(),
                url: z.string().describe('Image URL.').optional(),
                height: z
                  .number()
                  .int()
                  .describe('Original image height.')
                  .optional(),
                width: z
                  .number()
                  .int()
                  .describe('Original image width.')
                  .optional(),
              })
              .describe("Contact's profile picture.")
              .optional(),
            email: z
              .string()
              .describe("Contact's email address.")
              .email()
              .optional()
              .nullable(),
            displayName: z
              .string()
              .describe(
                "Contact's first and last name. If no name is provided, uses contact's email."
              )
              .optional()
              .nullable(),
          })
          .describe('Contact information.')
          .optional(),
        pointsExpiration: z
          .object({
            expirationDate: z
              .date()
              .describe('Date and time at which points are expiring.')
              .optional()
              .nullable(),
            expiringPointsAmount: z
              .number()
              .int()
              .describe(
                'Amount of points that are going to expire at that date.'
              )
              .optional(),
          })
          .describe('Points expiration information.')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Details on the paged set of results returned.')
    .optional(),
});
export const SearchAccountsRequest = z.object({
  search: z
    .object({
      filter: z
        .object({
          'contact._id': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'contact.name': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'contact.email': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'contact.displayName': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'points.balance': z
            .object({
              $eq: z.number(),
              $exists: z.boolean(),
              $gt: z.number(),
              $gte: z.number(),
              $hasAll: z.array(z.number()),
              $hasSome: z.array(z.number()),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
              $nin: z.array(z.number()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'points.redeemed': z
            .object({
              $eq: z.number(),
              $exists: z.boolean(),
              $gt: z.number(),
              $gte: z.number(),
              $hasAll: z.array(z.number()),
              $hasSome: z.array(z.number()),
              $in: z.array(z.number()),
              $lt: z.number(),
              $lte: z.number(),
              $ne: z.number(),
              $nin: z.array(z.number()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          lastActivityDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'pointsExpiration.expirationDate': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                'contact._id',
                'contact.name',
                'contact.email',
                'contact.displayName',
                'points.balance',
                'points.redeemed',
                '_updatedDate',
                'lastActivityDate',
                'pointsExpiration.expirationDate',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Search options.'),
});
export const SearchAccountsResponse = z.object({
  accounts: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Account ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        contactId: z
          .string()
          .describe(
            "Account owner's contact ID. See the Contacts API to learn more about a site's contacts."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        memberId: z
          .string()
          .describe(
            "Member ID of the account owner. See the Members API to learn more about a site's members."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        points: z
          .object({
            balance: z
              .number()
              .int()
              .describe(
                'Current points balance.\n\nEqual to the sum of `earned` and `adjusted` points, minus `redeemed` points.'
              )
              .min(0)
              .optional(),
            earned: z
              .number()
              .int()
              .describe('Total earned points.')
              .min(0)
              .optional(),
            adjusted: z
              .number()
              .int()
              .describe('Total adjusted points.')
              .min(0)
              .optional(),
            redeemed: z
              .number()
              .int()
              .describe('Total redeemed points.')
              .min(0)
              .optional(),
            expired: z
              .number()
              .int()
              .describe('Total expired points.')
              .min(0)
              .optional(),
          })
          .describe("Details of the account's point totals.")
          .optional(),
        rewardAvailable: z
          .boolean()
          .describe(
            'Whether the account has enough points in `points.balance` to redeem for a reward.'
          )
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time the account was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the account was last updated.')
          .optional()
          .nullable(),
        lastActivityDate: z
          .date()
          .describe("Date and time of the account's last activity.")
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^\d+$/, 'Must be a valid UInt64 string')
          .describe(
            'Revision number, which increments by 1 each time the loyalty account is updated.\n\nTo prevent conflicting changes, the current `revision` must be passed when updating the loyalty account.\n\nIgnored when creating an account.'
          )
          .optional(),
        tier: z
          .object({
            _id: z
              .string()
              .describe(
                'Tier ID.\n\nThis field will not be returned if the account belongs to the base tier.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date and time the tier information was last recalculated.'
              )
              .optional()
              .nullable(),
            points: z
              .number()
              .int()
              .describe(
                "Points earned and adjusted over the tier's current rolling window."
              )
              .min(0)
              .optional(),
          })
          .describe(
            'Tier information.\n\nSee the Tiers API for more information.'
          )
          .optional(),
        contact: z
          .object({
            _id: z
              .string()
              .describe('Contact ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe("Contact's first and last name.")
              .optional()
              .nullable(),
            picture: z
              .object({
                _id: z.string().describe('WixMedia image ID.').optional(),
                url: z.string().describe('Image URL.').optional(),
                height: z
                  .number()
                  .int()
                  .describe('Original image height.')
                  .optional(),
                width: z
                  .number()
                  .int()
                  .describe('Original image width.')
                  .optional(),
              })
              .describe("Contact's profile picture.")
              .optional(),
            email: z
              .string()
              .describe("Contact's email address.")
              .email()
              .optional()
              .nullable(),
            displayName: z
              .string()
              .describe(
                "Contact's first and last name. If no name is provided, uses contact's email."
              )
              .optional()
              .nullable(),
          })
          .describe('Contact information.')
          .optional(),
        pointsExpiration: z
          .object({
            expirationDate: z
              .date()
              .describe('Date and time at which points are expiring.')
              .optional()
              .nullable(),
            expiringPointsAmount: z
              .number()
              .int()
              .describe(
                'Amount of points that are going to expire at that date.'
              )
              .optional(),
          })
          .describe('Points expiration information.')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
  aggregationData: z
    .object({
      results: z
        .array(
          z.intersection(
            z.object({
              name: z
                .string()
                .describe(
                  'Aggregation name, returned in `aggregations.results.name`.'
                )
                .max(100)
                .optional(),
              type: z
                .enum(['VALUE', 'RANGE', 'SCALAR', 'DATE_HISTOGRAM', 'NESTED'])
                .describe(
                  'Aggregation type. Must align with the corresponding aggregation field.'
                )
                .optional(),
              fieldPath: z
                .string()
                .describe(
                  'Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.'
                )
                .max(200)
                .optional(),
            }),
            z.xor([
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalarDate: z.never().optional(),
              }),
              z.object({
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalarDate: z.never().optional(),
                values: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field.')
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Number of entities with this value.')
                            .optional(),
                        })
                      )
                      .max(250)
                      .optional(),
                  })
                  .describe('Value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalarDate: z.never().optional(),
                ranges: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          from: z
                            .number()
                            .describe('Inclusive lower bound of the range.')
                            .optional()
                            .nullable(),
                          to: z
                            .number()
                            .describe('Exclusive upper bound of the range.')
                            .optional()
                            .nullable(),
                          count: z
                            .number()
                            .int()
                            .describe('Total number of entities in this range.')
                            .optional(),
                        })
                      )
                      .max(50)
                      .optional(),
                  })
                  .describe('Range aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalarDate: z.never().optional(),
                scalar: z
                  .object({
                    type: z
                      .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                      .describe('Type of scalar aggregation.')
                      .optional(),
                    value: z
                      .number()
                      .describe('Value of the scalar aggregation.')
                      .optional(),
                  })
                  .describe('Scalar aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalarDate: z.never().optional(),
                groupedByValue: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field.')
                            .max(1000)
                            .optional(),
                          nestedResults: z
                            .intersection(
                              z.object({
                                name: z
                                  .string()
                                  .describe(
                                    'Unique, caller-defined aggregation name, identifiable by the requested aggregation `name`.'
                                  )
                                  .max(100)
                                  .optional(),
                                type: z
                                  .enum([
                                    'VALUE',
                                    'RANGE',
                                    'SCALAR',
                                    'DATE_HISTOGRAM',
                                    'NESTED',
                                  ])
                                  .describe('Aggregation type.')
                                  .optional(),
                                fieldPath: z
                                  .string()
                                  .describe(
                                    'Field which the data was aggregated by.'
                                  )
                                  .max(200)
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                }),
                                z.object({
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                  values: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            value: z
                                              .string()
                                              .describe('Value of the field.')
                                              .max(100)
                                              .optional(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Number of entities with this value.'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(250)
                                        .optional(),
                                    })
                                    .describe('Value aggregation results.'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  scalar: z.never().optional(),
                                  ranges: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            from: z
                                              .number()
                                              .describe(
                                                'Inclusive lower bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            to: z
                                              .number()
                                              .describe(
                                                'Exclusive upper bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Total number of entities in this range.'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(50)
                                        .optional(),
                                    })
                                    .describe('Range aggregation results.'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z
                                    .object({
                                      type: z
                                        .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                                        .describe('Type of scalar aggregation.')
                                        .optional(),
                                      value: z
                                        .number()
                                        .describe(
                                          'Value of the scalar aggregation.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Scalar aggregation results.'),
                                }),
                              ])
                            )
                            .describe('Nested aggregations.')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Group by value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                nested: z.never().optional(),
                scalarDate: z.never().optional(),
                dateHistogram: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe(
                              'Date in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format.'
                            )
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Number of entities in the bucket.')
                            .optional(),
                        })
                      )
                      .max(200)
                      .optional(),
                  })
                  .describe('Date histogram aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                scalarDate: z.never().optional(),
                nested: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          results: z
                            .record(
                              z.string(),
                              z.intersection(
                                z.object({}),
                                z.xor([
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalarDate: z.never().optional(),
                                  }),
                                  z.object({
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalarDate: z.never().optional(),
                                    value: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field.')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Number of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Value aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalarDate: z.never().optional(),
                                    range: z
                                      .object({
                                        from: z
                                          .number()
                                          .describe(
                                            'Inclusive lower bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        to: z
                                          .number()
                                          .describe(
                                            'Exclusive upper bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Number of entities in this range.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Range aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalarDate: z.never().optional(),
                                    scalar: z
                                      .object({
                                        value: z
                                          .number()
                                          .describe(
                                            'Value of the scalar aggregation.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Scalar aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    scalarDate: z.never().optional(),
                                    dateHistogram: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field.')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Number of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Date histogram aggregation result.'
                                      ),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalarDate: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Date in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format.'
                                          )
                                          .max(100)
                                          .optional(),
                                      })
                                      .describe(
                                        'Scalar date aggregation result.'
                                      ),
                                  }),
                                ])
                              )
                            )
                            .describe('List of nested aggregations.')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Nested aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalarDate: z
                  .object({
                    type: z
                      .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                      .describe(
                        'Type of scalar date aggregation. Possible values: `MIN`, `MAX`.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'Date in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format.'
                      )
                      .max(100)
                      .optional(),
                  })
                  .describe('Date aggregation results.'),
              }),
            ])
          )
        )
        .max(10000)
        .optional(),
    })
    .describe('Aggregation data')
    .optional(),
});
export const CountAccountsRequest = z.object({
  options: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe('Filter object.')
        .optional()
        .nullable(),
      search: z
        .object({
          mode: z.enum(['OR', 'AND']).optional(),
          expression: z
            .string()
            .describe('Search term or expression.')
            .max(100)
            .optional()
            .nullable(),
          fields: z.array(z.string()).max(20).optional(),
          fuzzy: z
            .boolean()
            .describe(
              'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
            )
            .optional(),
        })
        .describe('Free text to match in searchable fields.')
        .optional(),
    })
    .optional(),
});
export const CountAccountsResponse = z.object({
  count: z.number().int().min(0).optional(),
});
export const BulkAdjustPointsRequest = z.object({
  options: z
    .intersection(
      z.object({
        search: z
          .intersection(
            z.object({
              filter: z
                .record(z.string(), z.any())
                .describe(
                  'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
                )
                .optional()
                .nullable(),
              sort: z
                .array(
                  z.object({
                    fieldName: z
                      .string()
                      .describe('Name of the field to sort by.')
                      .max(512)
                      .optional(),
                    order: z.enum(['ASC', 'DESC']).optional(),
                  })
                )
                .max(10)
                .optional(),
              aggregations: z
                .array(
                  z.intersection(
                    z.object({
                      name: z
                        .string()
                        .describe(
                          'Aggregation name, returned in `aggregations.results.name`.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                      type: z
                        .enum([
                          'VALUE',
                          'RANGE',
                          'SCALAR',
                          'DATE_HISTOGRAM',
                          'NESTED',
                        ])
                        .optional(),
                      fieldPath: z
                        .string()
                        .describe(
                          'Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.'
                        )
                        .max(200)
                        .optional(),
                      groupBy: z
                        .intersection(
                          z.object({
                            name: z
                              .string()
                              .describe(
                                'Unique, caller-defined aggregation name, returned in `aggregations.results`.'
                              )
                              .max(100)
                              .optional()
                              .nullable(),
                            fieldPath: z
                              .string()
                              .describe('Field to aggregate by.')
                              .max(200)
                              .optional(),
                          }),
                          z.xor([
                            z.object({ value: z.never().optional() }),
                            z.object({
                              value: z
                                .intersection(
                                  z.object({
                                    sortType: z
                                      .enum(['COUNT', 'VALUE'])
                                      .describe('Sort type.')
                                      .optional(),
                                    sortDirection: z
                                      .enum(['DESC', 'ASC'])
                                      .describe('Sort direction.')
                                      .optional(),
                                    limit: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Number of aggregation results to return.\nMin: `1`\nMax: `250`\nDefault: `10`'
                                      )
                                      .optional()
                                      .nullable(),
                                    missingValues: z
                                      .enum(['EXCLUDE', 'INCLUDE'])
                                      .describe(
                                        'Whether to include or exclude missing values in the aggregation results.\nDefault: `EXCLUDE`.'
                                      )
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      includeOptions: z.never().optional(),
                                    }),
                                    z.object({
                                      includeOptions: z
                                        .object({
                                          addToBucket: z
                                            .string()
                                            .describe(
                                              'Specify a custom name for the bucket containing the missing values. Defaults are `"N/A"` for strings, `0` for integers, and `false` for booleans.'
                                            )
                                            .max(20)
                                            .optional(),
                                        })
                                        .describe(
                                          'Options for including missing values in results.'
                                        ),
                                    }),
                                  ])
                                )
                                .describe('Value aggregation configuration.'),
                            }),
                          ])
                        )
                        .describe('Deprecated. Use `nested` instead.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        value: z.never().optional(),
                        range: z.never().optional(),
                        scalar: z.never().optional(),
                        dateHistogram: z.never().optional(),
                        nested: z.never().optional(),
                      }),
                      z.object({
                        range: z.never().optional(),
                        scalar: z.never().optional(),
                        dateHistogram: z.never().optional(),
                        nested: z.never().optional(),
                        value: z
                          .intersection(
                            z.object({
                              sortType: z
                                .enum(['COUNT', 'VALUE'])
                                .describe('Sort type.')
                                .optional(),
                              sortDirection: z
                                .enum(['DESC', 'ASC'])
                                .describe('Sort direction.')
                                .optional(),
                              limit: z
                                .number()
                                .int()
                                .describe(
                                  'Number of aggregation results to return.\nMin: `1`\nMax: `250`\nDefault: `10`'
                                )
                                .optional()
                                .nullable(),
                              missingValues: z
                                .enum(['EXCLUDE', 'INCLUDE'])
                                .describe(
                                  'Whether to include or exclude missing values in the aggregation results.\nDefault: `EXCLUDE`.'
                                )
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                includeOptions: z.never().optional(),
                              }),
                              z.object({
                                includeOptions: z
                                  .object({
                                    addToBucket: z
                                      .string()
                                      .describe(
                                        'Specify a custom name for the bucket containing the missing values. Defaults are `"N/A"` for strings, `0` for integers, and `false` for booleans.'
                                      )
                                      .max(20)
                                      .optional(),
                                  })
                                  .describe(
                                    'Options for including missing values in results.'
                                  ),
                              }),
                            ])
                          )
                          .describe(
                            'A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of products for each price listed in the store.'
                          ),
                      }),
                      z.object({
                        value: z.never().optional(),
                        scalar: z.never().optional(),
                        dateHistogram: z.never().optional(),
                        nested: z.never().optional(),
                        range: z
                          .object({
                            buckets: z
                              .array(
                                z.object({
                                  from: z
                                    .number()
                                    .describe(
                                      'Inclusive lower bound of the range. Required if `to` is not provided.'
                                    )
                                    .optional()
                                    .nullable(),
                                  to: z
                                    .number()
                                    .describe(
                                      'Exclusive upper bound of the range. Required if `from` is not provided.'
                                    )
                                    .optional()
                                    .nullable(),
                                })
                              )
                              .max(50)
                              .optional(),
                          })
                          .describe(
                            'A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more.'
                          ),
                      }),
                      z.object({
                        value: z.never().optional(),
                        range: z.never().optional(),
                        dateHistogram: z.never().optional(),
                        nested: z.never().optional(),
                        scalar: z
                          .object({
                            type: z
                              .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                              .describe(
                                'Operator type for the scalar aggregation.'
                              )
                              .optional(),
                          })
                          .describe(
                            'A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`.'
                          ),
                      }),
                      z.object({
                        value: z.never().optional(),
                        range: z.never().optional(),
                        scalar: z.never().optional(),
                        nested: z.never().optional(),
                        dateHistogram: z
                          .object({
                            interval: z
                              .enum([
                                'YEAR',
                                'MONTH',
                                'WEEK',
                                'DAY',
                                'HOUR',
                                'MINUTE',
                                'SECOND',
                              ])
                              .describe(
                                'Interval for date histogram aggregation.'
                              )
                              .optional(),
                          })
                          .describe(
                            'A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.) For example, use a date histogram to find how many reservations have been made at a restaurant each week.'
                          ),
                      }),
                      z.object({
                        value: z.never().optional(),
                        range: z.never().optional(),
                        scalar: z.never().optional(),
                        dateHistogram: z.never().optional(),
                        nested: z
                          .object({
                            nestedAggregations: z
                              .array(
                                z.intersection(
                                  z.object({
                                    name: z
                                      .string()
                                      .describe(
                                        'Unique, caller-defined aggregation name, returned in `aggregations.results`.'
                                      )
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                    type: z
                                      .enum([
                                        'VALUE',
                                        'RANGE',
                                        'SCALAR',
                                        'DATE_HISTOGRAM',
                                      ])
                                      .optional(),
                                    fieldPath: z
                                      .string()
                                      .describe(
                                        'Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.'
                                      )
                                      .max(200)
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      value: z.never().optional(),
                                      range: z.never().optional(),
                                      scalar: z.never().optional(),
                                      dateHistogram: z.never().optional(),
                                    }),
                                    z.object({
                                      range: z.never().optional(),
                                      scalar: z.never().optional(),
                                      dateHistogram: z.never().optional(),
                                      value: z
                                        .intersection(
                                          z.object({
                                            sortType: z
                                              .enum(['COUNT', 'VALUE'])
                                              .describe('Sort type.')
                                              .optional(),
                                            sortDirection: z
                                              .enum(['DESC', 'ASC'])
                                              .describe('Sort direction.')
                                              .optional(),
                                            limit: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Number of aggregation results to return.\nMin: `1`\nMax: `250`\nDefault: `10`'
                                              )
                                              .optional()
                                              .nullable(),
                                            missingValues: z
                                              .enum(['EXCLUDE', 'INCLUDE'])
                                              .describe(
                                                'Whether to include or exclude missing values in the aggregation results.\nDefault: `EXCLUDE`.'
                                              )
                                              .optional(),
                                          }),
                                          z.xor([
                                            z.object({
                                              includeOptions: z
                                                .never()
                                                .optional(),
                                            }),
                                            z.object({
                                              includeOptions: z
                                                .object({
                                                  addToBucket: z
                                                    .string()
                                                    .describe(
                                                      'Specify a custom name for the bucket containing the missing values. Defaults are `"N/A"` for strings, `0` for integers, and `false` for booleans.'
                                                    )
                                                    .max(20)
                                                    .optional(),
                                                })
                                                .describe(
                                                  'Options for including missing values in results.'
                                                ),
                                            }),
                                          ])
                                        )
                                        .describe(
                                          "A value aggregation calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of orders for each order status."
                                        ),
                                    }),
                                    z.object({
                                      value: z.never().optional(),
                                      scalar: z.never().optional(),
                                      dateHistogram: z.never().optional(),
                                      range: z
                                        .object({
                                          buckets: z
                                            .array(
                                              z.object({
                                                from: z
                                                  .number()
                                                  .describe(
                                                    'Inclusive lower bound of the range. Required if `to` is not provided.'
                                                  )
                                                  .optional()
                                                  .nullable(),
                                                to: z
                                                  .number()
                                                  .describe(
                                                    'Exclusive upper bound of the range. Required if `from` is not provided.'
                                                  )
                                                  .optional()
                                                  .nullable(),
                                              })
                                            )
                                            .max(50)
                                            .optional(),
                                        })
                                        .describe(
                                          'A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria.'
                                        ),
                                    }),
                                    z.object({
                                      value: z.never().optional(),
                                      range: z.never().optional(),
                                      dateHistogram: z.never().optional(),
                                      scalar: z
                                        .object({
                                          type: z
                                            .enum([
                                              'COUNT_DISTINCT',
                                              'MIN',
                                              'MAX',
                                            ])
                                            .describe(
                                              'Operator type for the scalar aggregation.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`.'
                                        ),
                                    }),
                                    z.object({
                                      value: z.never().optional(),
                                      range: z.never().optional(),
                                      scalar: z.never().optional(),
                                      dateHistogram: z
                                        .object({
                                          interval: z
                                            .enum([
                                              'YEAR',
                                              'MONTH',
                                              'WEEK',
                                              'DAY',
                                              'HOUR',
                                              'MINUTE',
                                              'SECOND',
                                            ])
                                            .describe(
                                              'Interval for date histogram aggregation.'
                                            )
                                            .optional(),
                                        })
                                        .describe(
                                          'A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). For example, use a date histogram to determine how many reservations have been made at a restaurant each week. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria.'
                                        ),
                                    }),
                                  ])
                                )
                              )
                              .min(2)
                              .max(3)
                              .optional(),
                          })
                          .describe(
                            'A nested aggregation is applied within the results of another aggregation. Rather than aggregating directly on the primary dataset, first group data using one aggregation and then apply another aggregation within each group. It allows for more complex analyses where you can summarize data at different levels of detail or hierarchy. For example, to get the number of products that are in stock and out of stock for each price listed, first perform a value aggregation on `discountedPriceNumeric`, and a second value aggregation on `inStock`.'
                          ),
                      }),
                    ])
                  )
                )
                .max(10)
                .optional(),
              search: z
                .object({
                  mode: z.enum(['OR', 'AND']).optional(),
                  expression: z
                    .string()
                    .describe('Search term or expression.')
                    .max(100)
                    .optional()
                    .nullable(),
                  fields: z.array(z.string()).max(20).optional(),
                  fuzzy: z
                    .boolean()
                    .describe(
                      'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
                    )
                    .optional(),
                })
                .describe('Free text to match in searchable fields.')
                .optional(),
              timeZone: z
                .string()
                .describe(
                  'Time zone to adjust date-time-based filters and aggregations, in ISO 8601 (including offsets) or IANA time zone database (including time zone IDs) format.\nApplies to all relevant filters and aggregations, unless overridden by providing timestamps including time zone. For example, "2023-12-20T10:52:34.795Z".'
                )
                .max(50)
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({ cursorPaging: z.never().optional() }),
              z.object({
                cursorPaging: z
                  .object({
                    limit: z
                      .number()
                      .int()
                      .describe(
                        'Maximum number of items to return in the results.'
                      )
                      .min(0)
                      .max(100)
                      .optional()
                      .nullable(),
                    cursor: z
                      .string()
                      .describe(
                        "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                      )
                      .max(16000)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).'
                  ),
              }),
            ])
          )
          .optional(),
      }),
      z.xor([
        z.object({
          balance: z.never().optional(),
          amount: z.never().optional(),
        }),
        z.object({
          amount: z.never().optional(),
          balance: z.number().int().min(0).max(999999999),
        }),
        z.object({
          balance: z.never().optional(),
          amount: z.number().int().min(-9999999).max(9999999),
        }),
      ])
    )
    .optional(),
});
export const BulkAdjustPointsResponse = z.object({
  asyncJobId: z
    .string()
    .describe(
      'ID of the job.\n\nUse this ID to call [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/get-async-job)\nto retrieve the job details and metadata.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
});
